<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <!-- Houses whose sizing the extra questions unlock. -->
    <div class="choice-step__brands">
      <span
        v-for="brand in brandsData"
        :key="brand.id"
        class="choice-step__brand"
      >
        <img
          class="choice-step__brand-mark"
          :src="brand.brand_name_logo"
          :alt="brand.name"
        >
      </span>
    </div>

    <div class="choice-step__options">
      <FairButton
        v-for="option in step.options"
        :key="option.value"
        :label="option.label"
        :variant="option.action === 'go_to_results' ? 'tonal' : 'primary'"
        size="m"
        :rounded="false"
        @click="handleChoice(option)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useSurveyModal } from '@/features/survey/store'
import { FairButton } from '@/ui/primitives/FairButton'
import type { SurveyStep, ChoiceOption } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'choice' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const router = useRouter()

const brandsData = [
  {
    id: 1,
    name: 'Balenciaga',
    logo: '/img/survey/brands_logo/image.png',
    brand_name_logo: '/img/survey/brands_name/Balenciaga.png'
  },
  {
    id: 2,
    name: 'Dior',
    logo: '/img/survey/brands_logo/image-1.png',
    brand_name_logo: '/img/survey/brands_name/Dior.png'
  },
  {
    id: 3,
    name: 'LV',
    logo: '/img/survey/brands_logo/image-2.png',
    brand_name_logo: '/img/survey/brands_name/LV.png'
  },
  {
    id: 4,
    name: 'Prada',
    logo: '/img/survey/brands_logo/image-3.png',
    brand_name_logo: '/img/survey/brands_name/Prada.png'
  }
]

async function handleChoice(option: ChoiceOption) {
  // Обновляем данные
  surveyStore.updateSurveyData(props.step.id, option.value)

  if (option.action === 'go_to_results') {
    // Отправляем данные на бекенд и редиректим на страницу результатов
    try {
      await surveyStore.submitSurvey()
      surveyStore.hideSurveyModal()
      router.push('/profile/sizes')
    } catch (error) {
      console.error('Error submitting survey:', error)
      // В случае ошибки все равно закрываем модалку и редиректим
      surveyStore.hideSurveyModal()
      router.push('/profile/sizes')
    }
  } else if (option.action === 'go_to_step' && option.targetStepId) {
    surveyStore.goToStepById(option.targetStepId)
  } else {
    // Ждем обновления реактивности перед переходом к следующему шагу
    // Это важно для условных шагов, которые становятся видимыми после обновления данных
    await nextTick()
    surveyStore.nextStep()
  }
}
</script>

<style scoped>
.choice-step__brands {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-32);
  padding-block: var(--space-16);
  border-block: 1px solid var(--color-line-hairline);
}

.choice-step__brand {
  display: inline-flex;
  align-items: center;
}

/* Wordmarks only — the round logos doubled the same name at half the legibility. */
.choice-step__brand-mark {
  height: 16px;
  width: auto;
  opacity: 0.6;
}

.choice-step__options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-8);
}

.choice-step__options :deep(.fair-button) {
  flex: 1 1 200px;
  justify-content: center;
  border-radius: var(--radius-control);
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 500;
}
</style>
